import React, { ReactNode } from 'react';
import { PromiseState } from 'react-refetch';

import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import ResultsList, { IEntity } from './ResultsList/ResultsList';
import SearchResultsLoader from 'components/utility/ContentLoader/SearchResultsLoader/SearchResultsLoader';

import { Section, Heading } from './ResultsSection.style';
import { NoResults } from 'components/utility/NoResults/NoResults';
import apiConnect from 'helpers/apiConnect';
import { IEntityInfo } from 'pages/Search';
import Box from 'components/elements/Box';

interface IResultsSectionProps {
  title: string;
  /** The current input of the search field */
  searchValue: string;
  /** How many results before you have to click 'Show more' */
  displayLimit?: number;
  /** The nested elements to be rendered inside ResultsSection */
  children: ReactNode;
  /** The entityType that we are displaying */
  entityType: string;
  /** The PromiseState containing fetched data and its state */
  resultSectionFetch: PromiseState<any>;
  /** Triggered when the add to list button is clicked */
  onAddToListClick: (entityInfo: IEntityInfo) => void;
  /** Triggered when the entity row is clicked */
  onOpenEntity?: (entityInfo: IEntity) => void;
}

/**
 * A self-contained data displaying section with it's own ErrorBoundary
 * and loading state. Abstracts away the API calls, load more functionality and error states
 * for all entity types. Not meant to be re-used except in the Search pages
 * (tracks, releases, artists, labels, ...)
 */
const ResultsSection: React.FC<IResultsSectionProps> = (props) => {
  const {
    title,
    searchValue,
    displayLimit,
    children,
    entityType,
    resultSectionFetch,
    onAddToListClick,
    onOpenEntity,
  } = props;

  const { pending, fulfilled, reason, value: resultValue } = resultSectionFetch;
  const hasNoResults = fulfilled && resultValue && resultValue.length === 0;

  const value =
    entityType === 'compilation'
      ? resultValue?.map((el: any) => ({ ...el, isCompilation: true }))
      : resultValue;

  return (
    <Section data-pw="result-section">
      <Box
        display="flex"
        justifyContent="flex-start"
        alignItems="flex-start"
        alignContent="stretch"
      >
        <Box alignSelf="auto" flex={0}>
          <Heading>{title}</Heading>
        </Box>
        <Box alignSelf="auto" flex={0} marginLeft="auto" minWidth="5em">
          <Heading>
            {entityType !== 'label' && !hasNoResults && 'Add to list'}
          </Heading>
        </Box>
      </Box>
      <ErrorBoundary error={reason} errorInfo={reason ? reason.cause : null}>
        {pending && <SearchResultsLoader />}

        {fulfilled && value && (
          <ResultsList
            data={displayLimit ? value.slice(0, displayLimit) : value}
            onAddToListClick={onAddToListClick}
            onOpenEntity={onOpenEntity}
          />
        )}
        {/* When searching 'all' the results are limited to 5.
         * However, if there are more results available, and the 'Show more'
         * button is passed as a child, it is rendered here.
         * The button isn't hardcoded because ResultsSection
         * is also used without children.
         */}
        {value && value.length > 5 && children && children}

        {hasNoResults && <NoResults searchValue={searchValue} />}
      </ErrorBoundary>
    </Section>
  );
};

const ConnectedResultsSection = apiConnect((props: any) => ({
  resultSectionFetch: props.url,
}))(ResultsSection as any);

export default ConnectedResultsSection;
